import React, { useEffect, useState } from 'react';
import {
  arrayRemove,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import {
  HCard,
  HBadge,
  HPrimaryButton,
  HSecondaryButton,
  SectionLabel,
  HigloIcon,
} from '../h';
import { useMock } from '../Shell/mockContext';

/* v9 — Incoming Higlo invites (no number in the spec; part of the 5.x flow).

   Subscribes live to `iglooInviteMessage/{primaryFamilyID}/iglooIDs` for the
   invitee, and for each pending iglooID also subscribes live to
     - iglooInfo/{iglooID}           (for name)
     - iglooInfo/{iglooID}/pending/{fid} (for sender familyID, role)
     - familyInfo/{senderFID}       (for sender's display name)
   so that any change on the sender's side (cancel, role change, rename)
   reflects in the invitee's UI in real time without a refresh.

   Accept (matches OLD HomeDashboard/Invites.acceptIglooInvite order):
     1. Delete pending doc.
     2. Delete notifier doc.
     3. Create familyInfo/{primaryFID}/igloos/{iglooID}
          = { name: 'NewIglooFrom' + senderName }.
        Per the OLD code, the recipient names the Higlo after the inviter's
        family by default — they can rename it later. The role the sender
        picked is already baked into iglooInfo arrays; we don't touch those
        on accept.

   Decline (matches OLD HomeDashboard/Invites.declineIglooInvite):
     1. Delete pending doc.
     2. Delete notifier doc.
     3. arrayRemove self from iglooInfo.controllers AND .members atomically.
*/
export function IncomingInvites({ primaryFamilyID }) {
  const mock = useMock();
  const [iglooIDs, setIglooIDs] = useState([]);
  // Per-iglooID live state: { iglooName, senderFID, senderName, role }
  const [inviteDetails, setInviteDetails] = useState({});

  // 1. Discover pending iglooIDs from the notifier collection.
  useEffect(() => {
    if (mock || !primaryFamilyID) return undefined;
    const unsub = onSnapshot(
      collection(db, 'iglooInviteMessage', primaryFamilyID, 'iglooIDs'),
      (snap) => {
        setIglooIDs(snap.docs.map((d) => d.id));
      },
      (err) => {
        if (err?.code !== 'permission-denied') {
          console.warn('IncomingInvites notifier listener error', err.code);
        }
      },
    );
    return () => unsub();
  }, [primaryFamilyID, mock]);

  // 2. For each pending iglooID, attach three live listeners (iglooInfo,
  //    pending doc, sender's familyInfo) that feed a per-iglooID slot of
  //    inviteDetails. Re-attaches when the iglooIDs set changes.
  useEffect(() => {
    if (mock || !primaryFamilyID) return undefined;
    const unsubs = [];
    const senderUnsubs = {}; // senderFID currently subscribed per iglooID

    const patch = (iglooID, partial) => {
      setInviteDetails((prev) => ({
        ...prev,
        [iglooID]: { ...(prev[iglooID] || {}), ...partial },
      }));
    };

    for (const iglooID of iglooIDs) {
      // 2a. iglooInfo — for the Higlo's display name.
      unsubs.push(
        onSnapshot(
          doc(db, 'iglooInfo', iglooID),
          (snap) => {
            if (!snap.exists()) {
              patch(iglooID, { iglooName: '' });
              return;
            }
            patch(iglooID, { iglooName: snap.data().name || '' });
          },
          (err) => {
            if (err?.code !== 'permission-denied') {
              console.warn('IncomingInvites iglooInfo listener', iglooID, err.code);
            }
          },
        ),
      );

      // 2b. pending doc — sender familyID and intended role. If the sender
      //     cancels, they delete both the pending doc AND the notifier, so
      //     we rely on the notifier listener (2a, above) to drop the row
      //     rather than second-guessing it here. Critically: we do NOT
      //     delete the notifier on `!snap.exists()`. The pending listener's
      //     first callback can fire with a stale "doesn't exist" cache read
      //     before the sender's new pending write propagates — killing the
      //     notifier there would re-break a just-resent invite.
      unsubs.push(
        onSnapshot(
          doc(db, 'iglooInfo', iglooID, 'pending', primaryFamilyID),
          (snap) => {
            if (!snap.exists()) {
              // Clear local details so the row is hidden while we wait for
              // the pending write to arrive (or for the notifier to be
              // cleaned up on an actual cancel).
              setInviteDetails((prev) => {
                if (!(iglooID in prev)) return prev;
                const next = { ...prev };
                next[iglooID] = { ...next[iglooID], senderFID: null };
                return next;
              });
              return;
            }
            const d = snap.data() || {};
            const senderFID = d.from || null;
            patch(iglooID, { senderFID, role: d.role || 'member' });

            // Swap the sender listener if the senderFID changed.
            if (senderUnsubs[iglooID]?.fid !== senderFID) {
              senderUnsubs[iglooID]?.unsub?.();
              if (senderFID) {
                const senderUnsub = onSnapshot(
                  doc(db, 'familyInfo', senderFID),
                  (senderSnap) => {
                    if (senderSnap.exists()) {
                      patch(iglooID, {
                        senderName: senderSnap.data().name || 'another family',
                      });
                    }
                  },
                  (err) => {
                    if (err?.code !== 'permission-denied') {
                      console.warn('IncomingInvites sender listener', senderFID, err.code);
                    }
                  },
                );
                senderUnsubs[iglooID] = { fid: senderFID, unsub: senderUnsub };
              } else {
                senderUnsubs[iglooID] = { fid: null, unsub: null };
              }
            }
          },
          (err) => {
            if (err?.code !== 'permission-denied') {
              console.warn('IncomingInvites pending listener', iglooID, err.code);
            }
          },
        ),
      );
    }

    return () => {
      unsubs.forEach((u) => { try { u(); } catch (_) {} });
      Object.values(senderUnsubs).forEach(({ unsub }) => {
        try { unsub?.(); } catch (_) {}
      });
    };
  }, [iglooIDs, primaryFamilyID, mock]);

  if (!primaryFamilyID) return null;
  if (iglooIDs.length === 0) return null;

  const accept = async (iglooID, details) => {
    try {
      await deleteDoc(
        doc(db, 'iglooInfo', iglooID, 'pending', primaryFamilyID),
      );
      await deleteDoc(
        doc(db, 'iglooInviteMessage', primaryFamilyID, 'iglooIDs', iglooID),
      );
      // Default-name the just-accepted Higlo so it's recognisable in the
      // invitee's list. The user can rename it any time via HigloRename.
      const senderLabel = details?.senderName || 'another family';
      const defaultName = details?.iglooName || `${senderLabel}'s Higlo`;
      await setDoc(
        doc(db, 'familyInfo', primaryFamilyID, 'igloos', iglooID),
        { name: defaultName },
      );
    } catch (err) {
      console.error('IncomingInvites accept error', iglooID, err);
    }
  };

  const decline = async (iglooID) => {
    try {
      await deleteDoc(
        doc(db, 'iglooInfo', iglooID, 'pending', primaryFamilyID),
      );
      await deleteDoc(
        doc(db, 'iglooInviteMessage', primaryFamilyID, 'iglooIDs', iglooID),
      );
      await updateDoc(doc(db, 'iglooInfo', iglooID), {
        controllers: arrayRemove(primaryFamilyID),
        members: arrayRemove(primaryFamilyID),
      });
    } catch (err) {
      console.error('IncomingInvites decline error', iglooID, err);
    }
  };

  return (
    <>
      <SectionLabel className="mb-2">Invites</SectionLabel>
      <div className="space-y-3 mb-4">
        {iglooIDs.map((iglooID) => {
          const details = inviteDetails[iglooID];
          // If the pending doc hasn't resolved yet (live listener still
          // warming up) skip the row. Once it resolves we render the row and
          // it updates in real time as the sender changes role / name.
          if (!details || !details.senderFID) return null;
          return (
            <HCard key={iglooID} className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <HigloIcon size="sm" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-slate-900 truncate">
                    {details.iglooName || `${details.senderName || 'another family'}'s Higlo`}
                  </div>
                  <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">
                    <strong>{details.senderName || 'another family'}</strong>{' '}
                    has invited you to join this Higlo as a{' '}
                    <HBadge
                      variant={details.role === 'controller' ? 'controller' : 'anotherHome'}
                      className="align-middle"
                    >
                      {details.role === 'controller' ? 'Controller' : 'Member'}
                    </HBadge>
                    .
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <HSecondaryButton onClick={() => decline(iglooID)}>
                  Decline
                </HSecondaryButton>
                <HPrimaryButton onClick={() => accept(iglooID, details)}>
                  Accept
                </HPrimaryButton>
              </div>
            </HCard>
          );
        })}
      </div>
    </>
  );
}
